import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Trash2, Copy, Printer, Send, Mail, MessageSquare, ShoppingBasket } from "lucide-react";
import { plannerAPI, sundayOf, ymd, addDays } from "@/lib/planner";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const CATEGORIES = ["Produce", "Dairy", "Meat & Seafood", "Pantry", "Frozen", "Bakery", "Beverages", "Other"];

/**
 * PlannerShopping — the user's "Basket": every ingredient from every meal
 * they've planted this week, auto-aggregated + grouped by aisle. The user
 * can edit (add / delete / check), then **print** it or **send** it to a
 * spouse via email or text so they never have to retype it.
 */
export default function PlannerShopping() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("week") ? new Date(params.get("week") + "T00:00:00") : sundayOf(new Date());
  const [weekStart, setWeekStart] = useState(initial);
  const [data, setData] = useState({ items: [], grouped: {} });
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newQty, setNewQty] = useState("");
  const [newCategory, setNewCategory] = useState("Other");
  const [sendOpen, setSendOpen] = useState(false);
  const sendMenuRef = useRef(null);

  const load = async (w = weekStart) => {
    setLoading(true);
    try {
      const d = await plannerAPI.shopping(ymd(w));
      setData(d);
    } catch { toast.error("Couldn't load your basket."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [weekStart]);
  useEffect(() => { setParams({ week: ymd(weekStart) }, { replace: true }); }, [weekStart]);

  // Close "Send" dropdown on outside click
  useEffect(() => {
    if (!sendOpen) return;
    const onDoc = (e) => { if (sendMenuRef.current && !sendMenuRef.current.contains(e.target)) setSendOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [sendOpen]);

  const toggle = async (item) => {
    // Optimistic
    setData((prev) => ({
      ...prev,
      items: prev.items.map((i) => (i.id === item.id ? { ...i, checked: !i.checked } : i)),
      grouped: Object.fromEntries(
        Object.entries(prev.grouped).map(([k, rows]) => [k, rows.map((i) => (i.id === item.id ? { ...i, checked: !i.checked } : i))])
      ),
    }));
    try {
      await plannerAPI.updateShopping(item.id, { checked: !item.checked });
    } catch { toast.error("Couldn't update. Reloading."); load(); }
  };

  const del = async (item) => {
    try { await plannerAPI.deleteShopping(item.id); load(); }
    catch { toast.error("Couldn't delete."); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await plannerAPI.addShopping({
        name: newName.trim(),
        qty: newQty.trim() || null,
        category: newCategory,
        week_start_date: ymd(weekStart),
      });
      setNewName(""); setNewQty("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add item.");
    }
  };

  const weekEnd = addDays(weekStart, 6);
  const label = `${weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${weekEnd.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  const shift = (n) => setWeekStart(addDays(weekStart, n * 7));
  const total = data.items.length;
  const checkedCount = data.items.filter((i) => i.checked).length;

  // --- Shared formatters (used by Copy / Print / Send) ---
  // Only include items the user hasn't already checked off — the goal is
  // "what's still needed at the store", not a full transcript. If everything
  // is checked, fall back to the full list so the payload is never empty.
  const buildPlainText = () => {
    const showAll = total > 0 && checkedCount === total;
    const lines = [];
    for (const cat of CATEGORIES) {
      const rows = (data.grouped?.[cat] || []).filter((i) => showAll || !i.checked);
      if (!rows.length) continue;
      lines.push(cat.toUpperCase());
      rows.forEach((i) => lines.push(`  - ${[i.qty, i.unit, i.name].filter(Boolean).join(" ")}`));
      lines.push("");
    }
    return lines.join("\n").trimEnd();
  };

  const copy = async () => {
    const text = buildPlainText();
    if (!text) { toast.error("Nothing to copy — your basket is empty."); return; }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Basket copied to clipboard.");
    } catch {
      // Fall back to a select-all textarea on browsers that block clipboard.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.top = "-1000px";
      document.body.appendChild(ta);
      ta.focus(); ta.select();
      try { document.execCommand("copy"); toast.success("Basket copied to clipboard."); }
      catch { toast.error("Couldn't copy — long-press or highlight the list to copy manually."); }
      finally { document.body.removeChild(ta); }
    }
  };

  const print = () => {
    // The browser prints only what's inside the .print-basket container
    // thanks to the @media print CSS block below. Everything else is
    // hidden. window.print is synchronous enough on all modern browsers
    // that we don't need to schedule anything.
    if (total === 0) { toast.error("Nothing to print — your basket is empty."); return; }
    window.print();
  };

  const sendEmail = () => {
    const body = buildPlainText();
    if (!body) { toast.error("Nothing to send — your basket is empty."); return; }
    const subject = `Grocery basket · ${label}`;
    // mailto: URI — opens the user's default mail client with the list
    // pre-filled. Works on desktop (Mail/Outlook/etc.) and mobile.
    const href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
    setSendOpen(false);
  };

  const sendText = () => {
    const body = buildPlainText();
    if (!body) { toast.error("Nothing to send — your basket is empty."); return; }
    // iOS uses sms:&body=… , Android sms:?body=… — the ampersand form
    // works on both when there's no recipient prefilled.
    const href = `sms:&body=${encodeURIComponent(body)}`;
    window.location.href = href;
    setSendOpen(false);
  };

  return (
    <div data-testid="planner-shopping">
      {/* Print-only CSS: hide all normal UI, then unhide + reset the
          print container. Uses a body-level class so it applies globally
          during the print snapshot. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .print-basket, .print-basket * { visibility: visible !important; }
          .print-basket { position: absolute; inset: 0; padding: 24px; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="flex items-start justify-between mb-3 gap-3 no-print">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.35em] text-[#A87F56]">MY BASKET</div>
          <h1 className="text-2xl sm:text-4xl text-[#2A2823] mt-1 leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{label}</h1>
          {total > 0 && (
            <div className="text-xs text-[#2A2823]/55 mt-1">{total - checkedCount} still needed · {checkedCount} of {total} done</div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button aria-label="Previous week" data-testid="planner-shopping-prev" onClick={() => shift(-1)} className="w-9 h-9 rounded-full border border-[#A87F56]/30 flex items-center justify-center hover:bg-[#A87F56]/10">
            <ChevronLeft className="w-4 h-4 text-[#3E4A37]" />
          </button>
          <button data-testid="planner-shopping-this-week" onClick={() => setWeekStart(sundayOf(new Date()))} className="hidden sm:inline-flex text-sm rounded-full border border-[#A87F56]/30 px-4 h-9 text-[#3E4A37] hover:bg-[#A87F56]/10 items-center">
            This week
          </button>
          <button aria-label="Next week" data-testid="planner-shopping-next" onClick={() => shift(1)} className="w-9 h-9 rounded-full border border-[#A87F56]/30 flex items-center justify-center hover:bg-[#A87F56]/10">
            <ChevronRight className="w-4 h-4 text-[#3E4A37]" />
          </button>
        </div>
      </div>

      {/* Mobile-only "This week" quick jump — the desktop version lives inline
          in the header row above. */}
      <div className="sm:hidden mb-4 no-print">
        <button data-testid="planner-shopping-this-week-mobile" onClick={() => setWeekStart(sundayOf(new Date()))} className="text-xs rounded-full border border-[#A87F56]/30 px-3 h-8 text-[#3E4A37] hover:bg-[#A87F56]/10">
          Jump to this week
        </button>
      </div>

      {/* Helper strip — makes the "meal plan → basket" pathway obvious the
          first time a new user lands here. Only shows when the basket is
          non-empty. */}
      {total > 0 && (
        <div className="mb-5 rounded-2xl bg-[#EDE5DA]/70 border border-[#A87F56]/25 px-4 py-3 text-sm text-[#2A2823]/75 flex items-start gap-3 no-print" data-testid="planner-shopping-help">
          <ShoppingBasket className="w-4 h-4 text-[#3E4A37] mt-0.5 shrink-0" strokeWidth={1.8} />
          <div className="flex-1">
            Every recipe you plant in <Link to={`/planner/meals?week=${ymd(weekStart)}`} className="text-[#3E4A37] underline underline-offset-2">this week's meal plan</Link> auto-fills your basket. Edit anything you already have, then print or send it to your partner.
          </div>
        </div>
      )}

      {/* Primary action row — Print / Send / Copy live together so the
          "I'm ready to shop" moment is one glance. On mobile they stack
          into a 3-column grid so all three stay tappable without
          horizontal scroll. */}
      <div className="mb-5 grid grid-cols-3 sm:flex sm:flex-wrap sm:items-center gap-2 no-print" data-testid="planner-shopping-actions">
        <Button
          onClick={print}
          data-testid="planner-shopping-print"
          disabled={total === 0}
          className="rounded-full h-10 bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] disabled:opacity-40 px-3 sm:px-5"
        >
          <Printer className="w-4 h-4 sm:mr-1.5" />
          <span className="hidden sm:inline">Print basket</span>
          <span className="sm:hidden ml-1.5 text-xs">Print</span>
        </Button>

        <div className="relative" ref={sendMenuRef}>
          <Button
            onClick={() => setSendOpen((v) => !v)}
            data-testid="planner-shopping-send"
            disabled={total === 0}
            variant="outline"
            className="w-full rounded-full h-10 border-[#3E4A37]/40 text-[#3E4A37] hover:bg-[#3E4A37]/10 bg-white disabled:opacity-40 px-3 sm:px-5"
          >
            <Send className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Send to a partner</span>
            <span className="sm:hidden ml-1.5 text-xs">Send</span>
          </Button>
          {sendOpen && (
            <div
              role="menu"
              data-testid="planner-shopping-send-menu"
              className="absolute z-30 mt-2 left-0 min-w-[220px] rounded-xl bg-white border border-[#A87F56]/30 shadow-[0_16px_40px_rgb(42,40,35,0.12)] py-1.5 text-sm text-[#2A2823]"
            >
              <button
                onClick={sendEmail}
                data-testid="planner-shopping-send-email"
                className="w-full text-left px-4 py-2.5 hover:bg-[#F7F3ED] flex items-center gap-2"
              >
                <Mail className="w-4 h-4 text-[#3E4A37]" /> Email
              </button>
              <button
                onClick={sendText}
                data-testid="planner-shopping-send-text"
                className="w-full text-left px-4 py-2.5 hover:bg-[#F7F3ED] flex items-center gap-2"
              >
                <MessageSquare className="w-4 h-4 text-[#3E4A37]" /> Text message
              </button>
            </div>
          )}
        </div>

        <Button
          onClick={copy}
          data-testid="planner-shopping-copy"
          variant="outline"
          disabled={total === 0}
          className="rounded-full h-10 border-[#A87F56]/40 text-[#3E4A37] hover:bg-[#A87F56]/10 bg-transparent disabled:opacity-40 px-3 sm:px-5"
        >
          <Copy className="w-4 h-4 sm:mr-1.5" />
          <span className="hidden sm:inline">Copy</span>
          <span className="sm:hidden ml-1.5 text-xs">Copy</span>
        </Button>
      </div>

      {/* Manual add — on mobile the name gets its own row so the field is
          finger-friendly; qty + category + add sit on row two. */}
      <form onSubmit={add} className="bg-white rounded-2xl border border-[#A87F56]/25 p-3 mb-6 no-print grid grid-cols-1 sm:flex sm:flex-wrap sm:items-center gap-2" data-testid="planner-shopping-add-form">
        <input
          data-testid="planner-shopping-add-name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Add an item…"
          className="w-full sm:flex-1 sm:min-w-[180px] rounded-full border border-[#A87F56]/30 px-4 h-10 sm:h-9 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#3E4A37]/25"
        />
        <div className="flex items-center gap-2">
          <input
            data-testid="planner-shopping-add-qty"
            value={newQty}
            onChange={(e) => setNewQty(e.target.value)}
            placeholder="Qty (2 lb)"
            className="w-24 sm:w-28 rounded-full border border-[#A87F56]/30 px-4 h-10 sm:h-9 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#3E4A37]/25"
          />
          <select
            data-testid="planner-shopping-add-category"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="flex-1 sm:flex-none rounded-full border border-[#A87F56]/30 px-3 h-10 sm:h-9 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#3E4A37]/25"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Button type="submit" data-testid="planner-shopping-add-submit" className="rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] h-10 sm:h-9 px-4 shrink-0">
            <Plus className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Add</span>
          </Button>
        </div>
      </form>

      {/* Everything below this line is what actually prints. */}
      <div className="print-basket">
        {/* Print-only header — invisible on screen (hidden by default),
            shown by the @media print rule via the .print-basket wrapper. */}
        <div className="hidden print:block mb-6">
          <div className="text-xs tracking-widest text-[#A87F56]">WILLOAH · MY BASKET</div>
          <div className="text-2xl mt-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{label}</div>
          <div className="text-xs text-[#2A2823]/60 mt-1">{total} items · {checkedCount} already done</div>
        </div>

        {loading ? (
          <div className="text-sm text-[#2A2823]/50 py-8 text-center no-print">Loading…</div>
        ) : total === 0 ? (
          <div className="bg-white rounded-2xl border border-[#A87F56]/25 py-12 text-center text-sm text-[#2A2823]/60 italic no-print" data-testid="planner-shopping-empty">
            Your basket is empty. <Link to={`/planner/meals?week=${ymd(weekStart)}`} className="not-italic text-[#3E4A37] underline underline-offset-2">Plant some meals</Link> and every ingredient will land here automatically.
          </div>
        ) : (
          <div className="space-y-4">
            {CATEGORIES.map((cat) => {
              const rows = data.grouped?.[cat];
              if (!rows?.length) return null;
              return (
                <section key={cat} className="bg-white rounded-2xl border border-[#A87F56]/25 p-4 sm:p-5 print:border-0 print:p-0 print:mb-3" data-testid={`planner-shopping-cat-${cat}`}>
                  <div className="text-[10px] tracking-[0.35em] text-[#A87F56] mb-3 print:text-black print:tracking-widest">{cat.toUpperCase()}</div>
                  <ul className="divide-y divide-[#A87F56]/15 print:divide-black/20">
                    {rows.map((i) => (
                      <li key={i.id} className="flex items-center gap-3 py-3 sm:py-2.5 print:py-1.5" data-testid={`planner-shopping-item-${i.id}`}>
                        <input
                          type="checkbox"
                          checked={i.checked}
                          onChange={() => toggle(i)}
                          data-testid={`planner-shopping-toggle-${i.id}`}
                          className="w-5 h-5 sm:w-4 sm:h-4 accent-[#3E4A37] print:accent-black shrink-0"
                        />
                        <div className={`flex-1 min-w-0 text-[15px] ${i.checked ? "line-through text-[#2A2823]/40" : "text-[#2A2823]"}`}>
                          <span className="break-words">{i.name}</span>
                          {(i.qty || i.unit) && (
                            <span className="ml-2 text-xs text-[#2A2823]/55">{[i.qty, i.unit].filter(Boolean).join(" ")}</span>
                          )}
                        </div>
                        {i.manual && <span className="text-[10px] tracking-wide text-[#A87F56] no-print hidden sm:inline">manual</span>}
                        <button onClick={() => del(i)} aria-label="Delete" data-testid={`planner-shopping-delete-${i.id}`} className="w-9 h-9 flex items-center justify-center -mr-1 text-[#2A2823]/35 hover:text-[#8a3a2a] no-print shrink-0">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
